import React, { useContext } from "react";
import { RESOURCE_NAME } from "common/authorization";
import { PUBLIC_USER_STATUS } from "common/constants";
import {
  useSuspendPublicUserMutation,
  useUnsuspendPublicUserMutation,
} from "../../../generated/graphql";
import { useStatusToasts } from "../../../hooks/useStatusToasts";
import { track } from "../../../utils/tracking";
import { AuthContext } from "../../Authorization/AuthContext";
import { DropdownMenu } from "../../Inputs";
import { ActionsProps } from "../../Inputs/DropdownMenu";
import { useApprovalDecisionModal } from "./ApprovalDecisionModal";
import { PublicUser } from "./PublicUsersTable";

export interface PublicUserActionsProps {
  publicUser: PublicUser;
  onUpdate: () => void;
}

const PublicUserActions = ({
  publicUser,
  onUpdate,
}: PublicUserActionsProps) => {
  const { authorized } = useContext(AuthContext);
  const { addSuccessToast, addErrorToast } = useStatusToasts();

  const [showApprovalDecisionModal, hideApprovalDecisionModal] =
    useApprovalDecisionModal({
      onCancel: () => {
        hideApprovalDecisionModal();
      },
      onUpdate,
      publicUser,
    });

  const [suspendPublicUser] = useSuspendPublicUserMutation({
    onCompleted: () => {
      track("Public User Suspended", {
        publicUserId: publicUser.id,
      });
      addSuccessToast(
        `${publicUser.firstName} ${publicUser.lastName} has been suspended.`,
      );
      onUpdate();
    },
    onError: () => {
      addErrorToast(
        "There was an error suspending this user. Please try again.",
      );
    },
  });

  const [unsuspendPublicUser] = useUnsuspendPublicUserMutation({
    onCompleted: () => {
      track("Public User Unsuspended", {
        publicUserId: publicUser.id,
      });
      addSuccessToast(
        `${publicUser.firstName} ${publicUser.lastName} has been unsuspended.`,
      );
      onUpdate();
    },
    onError: () => {
      addErrorToast(
        "There was an error unsuspending this user. Please try again.",
      );
    },
  });

  const canManageStatus = authorized({
    permission: "manageStatus",
    resource: RESOURCE_NAME.PUBLIC_USER,
  });

  const actions: Array<ActionsProps> = [];

  if (
    canManageStatus &&
    publicUser.userGroups.some(group => group.requiresApproval) &&
    publicUser.status !== PUBLIC_USER_STATUS.SUSPENDED &&
    publicUser.status !== PUBLIC_USER_STATUS.UNCONFIRMED
  ) {
    actions.push({
      label: "Manage Approval",
      onClick: showApprovalDecisionModal,
    });
  }

  if (canManageStatus && publicUser.status === PUBLIC_USER_STATUS.ACTIVE) {
    actions.push({
      label: "Suspend",
      onClick: () => {
        void suspendPublicUser({ variables: { publicUserId: publicUser.id } });
      },
    });
  }

  if (canManageStatus && publicUser.status === PUBLIC_USER_STATUS.SUSPENDED) {
    actions.push({
      label: "Unsuspend",
      onClick: () => {
        void unsuspendPublicUser({
          variables: { publicUserId: publicUser.id },
        });
      },
    });
  }

  if (actions.length === 0) {
    return null;
  }

  return <DropdownMenu actions={actions} data-testid="public-user-actions" />;
};

export default PublicUserActions;
